<template>
  <div class="min-h-screen bg-black text-white py-4 px-4 md:px-8 md:mt-16">
    <div
      id="assessment-print"
      ref="printAreaRef"
      class="max-w-6xl mx-auto px-4 py-6 print:px-0"
    >

      <!-- dados do aluno no printer -->
      <section class="hidden print:grid md:grid-cols-2 print:gap-6 print:items-stretch mb-8">
        <div
          class="rounded-xl border border-gray-700 bg-neutral-900/60 p-5 flex flex-col justify-between"
        >
          <div>
            <h1
              class="text-2xl md:text-3xl font-semibold text-white tracking-wider"
            >
              {{ customer?.name }}
            </h1>
            <p class="text-gray-400 text-sm">
              usuário •
              {{ customer?.email }}
            </p>
          </div>
        </div>
      </section>

      <!-- HEADER -->
      <section class="grid md:grid-cols-2 gap-6 items-stretch">
        <!-- Nota + barra -->
        <div
          class="rounded-xl border border-gray-700 bg-neutral-900/60 p-5 flex flex-col justify-between"
        >
          <div>
            <h1
              class="text-2xl md:text-3xl font-semibold text-white tracking-wider"
            >
              Resultado da Avaliação
            </h1>
            <p class="text-gray-400 text-sm">
              Tentativa #{{ attempt?.id }} •
              {{ prettyDate(attempt?.completed_at) }}
            </p>
          </div>

          <div class="mt-6">
            <div class="text-5xl font-extrabold text-white leading-none">
              {{ attempt?.score ?? 0 }}
              <span class="text-2xl text-gray-400"
                >/ {{ attempt?.max_score ?? 0 }}</span
              >
            </div>
            <p class="mt-1 text-gray-300">Sua pontuação</p>

            <!-- barra de progresso -->
            <div class="mt-3">
              <div
                class="h-3 w-full bg-gray-700/70 rounded-full overflow-hidden"
              >
                <div
                  class="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  :style="{ width: percent + '%' }"
                />
              </div>
              <div class="mt-2 text-sm text-gray-400 flex items-center gap-2">
                <span :class="pillClass">{{ statusLabel }}</span>
                <span class="ml-auto">{{ percent }}%</span>
              </div>
            </div>
          </div>

          <!-- ações -->
          <div class="mt-6 flex flex-col sm:flex-row gap-3 print:hidden">
            <button
              class="w-full rounded-lg py-3 text-2xl md:text-xl text-center focus:outline-none uppercase text-white font-bold tracking-wide bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300"
              @click="exportPDF"
            >
              Exportar PDF
            </button>
            <button
              class="w-full rounded-lg py-3 text-2xl md:text-xl text-center focus:outline-none uppercase text-white font-bold tracking-wide bg-gray-800 border-2 border-gray-700 hover:bg-white hover:text-gray-800 transition-colors duration-300"
              @click="goBack"
            >
              Voltar
            </button>
          </div>
        </div>

        <!-- Manômetro -->
        <div
          class="rounded-xl border border-gray-700 bg-neutral-900/60 p-5 flex items-center justify-center print:hidden"
        >
          <svg viewBox="0 0 200 120" class="w-full max-w-md">
            <!-- fundo semicircular -->
            <defs>
              <linearGradient id="grad-gauge" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#ef4444" />
                <!-- red-500 -->
                <stop offset="33%" stop-color="#f59e0b" />
                <!-- amber-500 -->
                <stop offset="66%" stop-color="#84cc16" />
                <!-- lime-500 -->
                <stop offset="100%" stop-color="#10b981" />
                <!-- emerald-500 -->
              </linearGradient>
            </defs>

            <!-- arco colorido -->
            <path
              d="M10 110 A90 90 0 0 1 190 110"
              stroke="url(#grad-gauge)"
              stroke-width="18"
              fill="none"
              stroke-linecap="round"
            />
            <!-- trilha cinza atrás (leve sombra) -->
            <path
              d="M10 110 A90 90 0 0 1 190 110"
              stroke="rgba(31,41,55,0.6)"
              stroke-width="22"
              fill="none"
              stroke-linecap="round"
              opacity="0.25"
            />

            <!-- ponteiro -->
            <g :transform="`rotate(${needleAngle} 100 110)`">
              <line
                x1="100"
                y1="110"
                x2="100"
                y2="28"
                stroke="white"
                stroke-width="3"
              />
              <circle cx="100" cy="110" r="5" fill="white" />
            </g>

            <!-- rótulos -->
            <!-- <text
              x="100"
              y="118"
              text-anchor="middle"
              fill="#9CA3AF"
              font-size="10"
            >
              0%
            </text> -->
            <text
              x="28"
              y="118"
              text-anchor="middle"
              fill="#9CA3AF"
              font-size="10"
            >
              0%
            </text>
            <text
              x="100"
              y="118"
              text-anchor="middle"
              fill="#9CA3AF"
              font-size="10"
              dy="-78"
            >
              50%
            </text>
            <text
              x="172"
              y="118"
              text-anchor="middle"
              fill="#9CA3AF"
              font-size="10"
            >
              100%
            </text>
            <!-- <text
              x="190"
              y="118"
              text-anchor="middle"
              fill="#9CA3AF"
              font-size="10"
            >
              100%
            </text> -->
          </svg>
        </div>
      </section>

      <!-- LISTA DE QUESTÕES -->
      <section
        class="mt-8 rounded-xl border border-gray-700 bg-neutral-900/60 overflow-hidden"
      >
        <header
          class="px-5 py-3 border-b border-gray-700 flex items-center justify-between"
        >
          <h2 class="text-white font-semibold tracking-wider text-xl">
            Questões
          </h2>
          <span class="text-sm text-gray-400">
            {{ correctCount }} corretas de {{ answers.length }}
          </span>
        </header>

        <div class="overflow-x-auto">
          <table class="min-w-full">
            <thead>
              <tr class="text-gray-400 text-center text-sm">
                <th class="px-5 py-3 font-medium w-16">#</th>
                <th class="px-5 py-3 font-medium">Pergunta</th>
                <th class="px-5 py-3 font-medium w-40">Status</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(a, idx) in answers"
                :key="a.question_id"
                class="border-t border-gray-800"
              >
                <td class="px-5 py-3 text-gray-300">{{ idx + 1 }}</td>
                <td class="px-5 py-3 text-base">
                  <p class="text-white">{{ a.title }}</p>
                  <p class="text-sm text-gray-400 mt-1 tracking-wide">
                    Sua resposta:
                    <span class="text-gray-300">{{
                      a.selected_option?.text || "—"
                    }}</span>
                    <template v-if="!a.is_correct">
                      • Correta:
                      <span class="text-gray-300">{{
                        a.correct_option?.text || "—"
                      }}</span>
                    </template>
                  </p>
                </td>
                <td class="px-5 py-3 tracking-wide">
                  <span
                    class="inline-flex items-center gap-2 text-xs font-semibold px-2.5 py-1 rounded-full"
                    :class="
                      a.is_correct
                        ? 'bg-emerald-900 text-emerald-300'
                        : 'bg-red-900 text-red-300'
                    "
                  >
                    <PhCheckCircle v-if="a.is_correct" class="w-4 h-4" />
                    <PhXCircle v-else class="w-4 h-4" />
                    {{ a.is_correct ? "CORRETA" : "INCORRETA" }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PhCheckCircle, PhXCircle } from "@phosphor-icons/vue";
import AssessmentService from "@/shared/services/assessment";
import xBanner from './../../../assets/logo/plataforma_x/xsolution_logo.png'
import appBanner from './../../../assets/logo/login-logo.png'
import { dataShared } from "@/shared/store";
// import AssessmentService from "@/services/AssessmentService";

const route = useRoute();
const router = useRouter();
const attemptId = route.params.attemptId;
const attempt = ref(null);
const answers = ref([]);
const printAreaRef = ref(null)

const customer = computed(() => dataShared.getters.getDataByUserLogged)

onMounted(async () => {
  window.scrollTo(0, 0)
  
  if(! customer.value)
    Promise.all([
      await dataShared.dispatch('fetchLoggedUser')
    ])

  // review da tentativa
  try {
    const { data } = await AssessmentService.getAttemptReview(attemptId);
    attempt.value = data.attempt;
    answers.value = data.answers || [];
  } catch (e) {
    router.replace({ name: "home" });
  }
});

// métricas
const correctCount = computed(
  () => answers.value.filter((a) => a.is_correct).length
);
const percent = computed(() => {
  const max = Number(attempt.value?.max_score ?? 0);
  const sc = Number(attempt.value?.score ?? 0);
  if (!max) return 0;
  return Math.round((sc / max) * 100);
});

// status + pílula
const statusLabel = computed(() => {
  const minReq = Number(attempt.value?.min_required ?? 0);
  const sc = Number(attempt.value?.score ?? 0);
  return sc >= minReq ? "APROVADO" : "REPROVADO";
});
const pillClass = computed(() =>
  statusLabel.value === "APROVADO"
    ? "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-900 text-emerald-300"
    : "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-900 text-red-300"
);

// manômetro: -90° (0%) → +90° (100%)
const needleAngle = computed(() => -90 + percent.value * 1.8);

function prettyDate(dt) {
  if (!dt) return "";
  try {
    const d = new Date(dt);
    return d.toLocaleString();
  } catch {
    return String(dt);
  }
}

// ações
function exportPDF ({ orientation = 'portrait', paper = 'A4' } = {}) {
  const area = printAreaRef.value
  if (!area) return

  const styleTags = [...document.querySelectorAll('link[rel="stylesheet"],style')]
    .map(n => n.outerHTML).join('\n')

  const pageCss = `
    @page { size: ${paper} ${orientation}; margin: 10mm 10mm; }
    html, body { background:#fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-border { border-color:#d1d5db !important; }
  `

  const html = `<!doctype html><html><head>
    <meta charset="utf-8" />
    <title>Resultado da Avaliação</title>
    ${styleTags}
    <style>${pageCss}</style>
  </head><body>
    <div class="max-w-6xl mx-auto px-6 py-6">
      <div class="flex items-center justify-between mb-6">
        <img src="${xBanner}" style="height:60px" />
        <img src="${appBanner}" style="height:60px" />
      </div>
      ${area.outerHTML}
    </div>
  </body></html>`

  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute('aria-hidden', 'true')
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow.document
  doc.open()
  doc.write(html)
  doc.close()

  // dá um “tick” p/ CSS/fonts aplicarem e imprime
  setTimeout(() => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
    setTimeout(() => document.body.removeChild(iframe), 500)
  }, 150)
}
function goBack() {
  if(route.params.tId)
    router.replace({
      name: "avaliable-info",
      params: { 
        id: route.params.tId 
      },
    });
    
  else 
    router.replace({
      name: "home"
    });
    
}
</script>

<style>
/* Impressão: plano de fundo claro e remove botão */
@media print {
  body {
    background: #fff !important;
  }
}
</style>
